import { Card } from "@/components/ui/card";
import { CarouselItem } from "@/components/ui/carousel";
import React, { useEffect, useState } from "react";
import { FareDialog } from "./fareDialog";
import { Button } from "@/components/ui/button";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import { useRouter } from "next/navigation";
import { formatDuration } from "@/lib/internalApis";
import { useFlightSearch } from "@/hooks/flight/useFlightSearch";
import { Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { logFirebaseEvent } from "@/utils/firebaseAnalytics";
import publicApi from "@/lib/publicApi";

const FareDetails = ({
  fares,
  totalPriceList,
  isMultiCity,
  isReturn,
  Combo,
  handlerFaresRulesBooks,
  sI,
  selectedFare,
  totalTravelTimeMinutes,
  readOnly = false,
  shareId,
  onSelectFare,
}: any) => {
  const [fareRules, setFareRules] = useState<any>();
  const { getFareRules, isLoading: isFareRuleLoading } = useFlightSearch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPublicFareRuleLoading, setIsPublicFareRuleLoading] =
    useState<boolean>(false);
  const tripType = new URLSearchParams(window.location.search).get("tripType");

  const fareRuleLoading = readOnly ? isPublicFareRuleLoading : isFareRuleLoading;

  useEffect(() => {
    if (!fares?.id) return;

    if (readOnly && shareId) {
      setIsPublicFareRuleLoading(true);
      publicApi
        .post(`/flight/shareable-links/${shareId}/farerule`, {
          id: fares.id,
          flowType: "SEARCH",
        })
        .then((res) => setFareRules(res.data))
        .catch(() => setFareRules(undefined))
        .finally(() => setIsPublicFareRuleLoading(false));
      return;
    }

    if (readOnly) return;

    getFareRules({ id: fares.id, flowType: "SEARCH" }).then((data: any) => {
      setFareRules(data);
    });
  }, [readOnly, shareId, fares?.id, getFareRules]);

  //   const key = Object.keys(fareRules?.fareRule);
  //   console.log(key)

  let totalPrice = fares.fd.ADULT.fC.TF;

  const {
    isViewOpen,
    setViewData,
    viewData,
    isEmailOpen,
    isWhatsAppOpen,
  }: any = useFlightBookingStore();

  return (
    <CarouselItem
      key={fares.fareIdentifier}
      className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3"
    >

      <Card
        className={`p-4 relative rounded h-full flex flex-col ${
          readOnly ? "cursor-pointer border-2" : "border"
        } ${
          readOnly && selectedFare?.id === fares.id
            ? "border-primary"
            : readOnly
              ? "border-border"
              : ""
        }`}
        onClick={
          readOnly && onSelectFare
            ? () => onSelectFare(fares)
            : undefined
        }
      >
        {/* Fare Type Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-sm text-muted-foreground">Fare Type</p>
          </div>
          <div>
            <span
              className={`inline-block px-2 py-0.5 mt-.5 text-[11px] font-medium rounded-md w-fit ${fares.fareIdentifier === "SALE"
                ? "bg-blue-300/80 text-blue-800"
                : fares.fareIdentifier === "TACTICAL"
                  ? "bg-blue-300/80 text-blue-800"
                  : fares.fareIdentifier === "COUPON"
                    ? "bg-blue-300/80 text-blue-800"
                    : fares.fareIdentifier === "INSTANT_OFFER_FARE"
                      ? "bg-pink-200/80 text-pink-800"
                      : fares.fareIdentifier === "OFFER_FARE_WITH_PNR"
                        ? "bg-pink-200/80 text-pink-800"
                        : fares.fareIdentifier === "ECO_FLEX"
                          ? "bg-purple-200/80 text-purple-800"
                          : fares.fareIdentifier === "EXPRESS_FLEX"
                            ? "bg-purple-200/80 text-purple-800"
                            : fares.fareIdentifier === "FLEXI"
                              ? "bg-purple-200/80 text-purple-800"
                              : fares.fareIdentifier === "FLEXI_PLUS"
                                ? "bg-purple-200/80 text-purple-800"
                                : fares.fareIdentifier === "TJ_FLEX"
                                  ? "bg-yellow-200/80 text-yellow-400"
                                  : fares.fareIdentifier === "ECO_VALUE"
                                    ? "bg-red-300/80 text-red-800"
                                    : fares.fareIdentifier === "ECO_CLASSIC"
                                      ? "bg-red-300/80 text-red-800"
                                      : fares.fareIdentifier === "EXPRESS_VALUE"
                                        ? "bg-red-300/80 text-red-800"
                                        : fares.fareIdentifier === "PUBLISHED"
                                          ? "bg-red-300/80 text-red-800"
                                          : fares.fareIdentifier === "SME"
                                            ? "bg-green-400/80 text-green-700"
                                            : fares.fareIdentifier === "CORPORATE_VALUE"
                                              ? "bg-green-200/80 text-green-800"
                                              : fares.fareIdentifier === "CORPORATE_FARE"
                                                ? "bg-green-200/80 text-green-800"
                                                : fares.fareIdentifier === "STRETCH"
                                                  ? "bg-yellow-100/80 text-yellow-600"
                                                  : fares.fareIdentifier === "STRETCH_PLUS"
                                                    ? "bg-yellow-100/80 text-yellow-600"
                                                    : "bg-yellow-200/80 text-yellow-600"
                }`}
            >
              {fares.fareIdentifier}
            </span>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-sm">Meal</span>
            <span className={`text-sm text-blue-600}`}>
              {fares?.fd?.ADULT?.mI ? "Free" : "Paid"}
            </span>
          </div>

          {/* Baggage Info */}
          {(fares.fd.ADULT?.bI?.iB || fares.fd.ADULT?.bI?.cB) && (
            <div className="flex justify-between items-center">
              <span className="text-sm">Baggage Info</span>
              <div className="flex items-center gap-2">
                {fares.fd.ADULT?.bI?.iB && (
                  <span className="text-primary">
                    {(fares.fd.ADULT?.bI?.iB).split("(")[0].trim()} |{" "}
                  </span>
                )}
                <span className="text-primary">{fares.fd.ADULT?.bI?.cB}</span>
              </div>
            </div>
          )}
          {(fareRules && Object.keys(fareRules?.fareRule).length > 0) && (
            <div className="flex justify-between items-center">
              <span className="text-sm">Cancellation Fee</span>
              {fareRuleLoading ? (
                <Skeleton className="h-4 w-[100px]" />
              ) : (
                Object?.keys(fareRules?.fareRule).map(
                  (key: any, index: number) => {
                    return (
                      <span key={index} className="text-primary">
                        {fares?.fareIdentifier === "OFFER_FARE_WITH_PNR" ? "Not Allowed" : `${fareRules?.fareRule[key].fr?.CANCELLATION?.DEFAULT
                          ?.amount
                          ? "Paid"
                          : "Free"}`}
                      </span>
                    );
                  }
                )
              )}
            </div>
          )}
          {(fareRules && Object.keys(fareRules?.fareRule).length > 0) && (
            <div className="flex justify-between items-center">
              <span className="text-sm">Date Change Fee</span>
              {fareRuleLoading ? (
                <span className="text-primary">
                  <Skeleton className="h-4 w-[100px]" />
                </span>
              ) : (
                Object?.keys(fareRules?.fareRule).map(
                  (key: any, index: number) => {
                    return (
                      <span key={index} className="text-primary">
                        {fares?.fareIdentifier === "OFFER_FARE_WITH_PNR" ? "Not Allowed" : `${fareRules?.fareRule[key].fr?.DATECHANGE?.DEFAULT
                          ?.amount
                          ? "Paid"
                          : "Free"}`}
                      </span>
                    );
                  }
                )
              )}
            </div>
          )}
          {(fareRules && Object.keys(fareRules?.fareRule).length > 0) && (
            <div className="flex justify-between items-center">
              <span className="text-sm">Seat Charge</span>
              {fareRuleLoading ? (
                <span className="text-primary">
                  <Skeleton className="h-4 w-[100px]" />
                </span>
              ) : (
                <span className="text-primary">As Per Airline</span>
              )}
            </div>
          )}
        </div>

        {/* Price and Seats */}
        <div className="mt-4 flex justify-between items-center">
          <div className="flex flex-col mb-2">
            <span className="text-2xl font-bold">
              ₹ {fares?.fd?.ADULT?.fC?.TF.toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground">per adult</span>
            {fares?.fd?.ADULT?.sR && (
              <span className="text-sm text-muted-foreground">
                {fares?.fd?.ADULT?.sR} Seats Left
              </span>
            )}
          </div>

          {fares?.fd?.ADULT?.rT === 0 && (
            <div className="text-red-500 bg-red-100 px-2 py-1 rounded-md">
              Non Refundable
            </div>
          )}
        </div>

        <div className="space-y-2 w-full mt-auto">
          <div className="flex justify-between items-center gap-2">
            {!readOnly && (
            <Button
              onClick={() => {
                handlerFaresRulesBooks(fares, totalPrice);
                setIsLoading(true);
                logFirebaseEvent("Book_Flight", {
                  fareIdentifier: fares.fareIdentifier,
                });
              }}
              className={`p-[8px_24px] rounded-[5px] h-[50px] mt-1 bg-primary text-white overflow-hidden border tracking-[0.1px] border-gray_silver transition-all before:ease relative hover:bg-primary hover:text-white before:absolute before:right-0 before:top-0 before:h-16 before:w-4 before:-translate-x-48 before:rotate-8 before:bg-white before:opacity-10 before:duration-1000 hover:before:translate-x-12 w-full text-[16px] block ${!selectedFare ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {tripType === "roundtrip" || isMultiCity ? (
                selectedFare?.id === fares.id ? (
                  "Selected"
                ) : (
                  "Select Fare"
                )
              ) : isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span>Book Flight</span>
                  <span className="text-[24px] font-bold ml-2">↝</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span>Book Flight</span>
                  <span className="text-[24px] font-bold ml-2">↝</span>
                </div>
              )}
            </Button>
            )}
            {readOnly && <div className="flex-1" />}
            {(!readOnly || fareRules) && (
            <div
              className="mt-2 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <FareDialog
                selectedFare={fares}
                totalPriceInfo={totalPriceList}
                fareRule={fareRules}
              />
            </div>
            )}
          </div>
          {!readOnly && (isViewOpen || isEmailOpen || isWhatsAppOpen ? (
            <div>
              <input
                type="checkbox"
                onClick={(e: any) => {
                  if (e.target.checked) {
                    setViewData([
                      ...viewData,
                      {
                        id: fares.id,
                        sI,
                        fares,
                        tripType: isMultiCity
                          ? "Multicity"
                          : isReturn
                            ? "Return"
                            : Combo === "Combo"
                              ? "Combo"
                              : "Onward",
                        duration: formatDuration(totalTravelTimeMinutes),
                        totalPrice: totalPrice,
                      },
                    ]);
                  } else {
                    setViewData(
                      viewData.filter((data: any) => data.id !== fares.id)
                    );
                  }
                }}
              />
              <span className="ml-2 text-center text-sm text-gray-500 italic">{`${isViewOpen
                ? "Share via View"
                : isEmailOpen
                  ? "Share via Email"
                  : isWhatsAppOpen
                    ? "Share via WhatsApp"
                    : ""
                }`}</span>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-500 italic">
              Select "View Details" to Share
            </div>
          ))}
        </div>
      </Card>
    </CarouselItem>
  );
};

export default FareDetails;
